import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Clock,
  Building2,
  TrendingUp,
  FileText
} from 'lucide-react';
import { ROUTES } from '../../constants/routes.constants';

/**
 * Page de détails d'une agence
 * Affiche les informations personnelles du vendeur et ses statistiques de réservation
 */
const AgencyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agencyData, setAgencyData] = useState(null);

  useEffect(() => {
    const fetchAgencyDetails = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('accessToken');
        
        if (!token) {
          throw new Error('Token d\'authentification manquant');
        }

        const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors du chargement des détails de l\'agence');
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          setAgencyData(result.data);
        } else {
          throw new Error('Données invalides reçues du serveur');
        }
      } catch (err) {
        console.error('Error fetching agency details:', err);
        setError(err.message || 'Erreur lors du chargement des détails de l\'agence');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchAgencyDetails();
    }
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (statut, vendorStatus) => {
    if (statut === 'Actif' || vendorStatus === 'APPROVED') {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 flex items-center gap-2 w-fit">
          <CheckCircle className="w-4 h-4" />
          Actif
        </span>
      );
    } else if (vendorStatus === 'PENDING') {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 flex items-center gap-2 w-fit">
          <Clock className="w-4 h-4" />
          En attente
        </span>
      );
    } else {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 flex items-center gap-2 w-fit">
          <XCircle className="w-4 h-4" />
          Inactif
        </span>
      );
    }
  };

  const getAbsoluteUrl = (relativePath) => {
    if (!relativePath || relativePath.startsWith('http')) {
      return relativePath;
    }
    return `${ROUTES.API_BASE_URL.replace('/api', '')}${relativePath}`;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#27489E] mb-4"></div>
            <p className="text-gray-600">Chargement des détails de l'agence...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800">{error}</p>
          </div>
          <Link
            to={ROUTES.ADMIN_AGENCIES}
            className="inline-flex items-center gap-2 text-[#27489E] hover:text-[#1e3a7a] transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la liste des agences
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  if (!agencyData) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <p className="text-gray-600">Aucune donnée disponible</p>
          <Link
            to={ROUTES.ADMIN_AGENCIES}
            className="inline-flex items-center gap-2 text-[#27489E] hover:text-[#1e3a7a] transition mt-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la liste des agences
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const { personalInfo, bookingStats } = agencyData;

  // Configuration des cartes de statistiques
  const statsCards = [
    {
      label: 'Total',
      value: bookingStats.total,
      color: 'blue',
      icon: FileText,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      status: null, // Pas de filtre pour "Total", juste vendorId
    },
    {
      label: 'En attente',
      value: bookingStats.pending,
      color: 'yellow',
      icon: Clock,
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600',
      borderColor: 'border-yellow-200',
      status: 'PENDING',
    },
    {
      label: 'Confirmées',
      value: bookingStats.confirmed,
      color: 'green',
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      borderColor: 'border-green-200',
      status: 'CONFIRMED',
    },
    {
      label: 'Annulées',
      value: bookingStats.cancelled,
      color: 'red',
      icon: XCircle,
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      borderColor: 'border-red-200',
      status: 'CANCELLED',
    },
    {
      label: 'Refusées',
      value: bookingStats.declined,
      color: 'orange',
      icon: XCircle,
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-200',
      status: 'DECLINED',
    },
    {
      label: 'Terminées',
      value: bookingStats.completed,
      color: 'emerald',
      icon: CheckCircle,
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      borderColor: 'border-emerald-200',
      status: 'COMPLETED',
    },
  ];

  // Fonction pour gérer le clic sur une carte KPI
  const handleKpiClick = (stat) => {
    const params = new URLSearchParams();
    params.append('vendorId', id);
    
    if (stat.status) {
      params.append('status', stat.status);
    }
    
    navigate(`${ROUTES.ADMIN_BOOKINGS}?${params.toString()}`);
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* En-tête avec bouton retour */}
        <div className="mb-6">
          <Link
            to={ROUTES.ADMIN_AGENCIES}
            className="inline-flex items-center gap-2 text-[#27489E] hover:text-[#1e3a7a] transition mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Retour à la liste</span>
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-[#27489E]" />
                {personalInfo.nom}
              </h1>
              <div className="mt-2">
                {getStatusBadge(personalInfo.statut, personalInfo.vendorStatus)}
              </div>
            </div>
          </div>
        </div>

        {/* Bloc 1 : Informations de l'Agence */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#27489E]" />
            Informations de l'Agence
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email */}
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500 mb-1 block">Email</label>
                <p className="text-gray-900 font-medium">{personalInfo.email}</p>
              </div>
            </div>

            {/* Téléphone */}
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500 mb-1 block">Téléphone</label>
                <p className="text-gray-900 font-medium">{personalInfo.telephone}</p>
              </div>
            </div>

            {/* Adresse */}
            <div className="flex items-start gap-3 md:col-span-2">
              <div className="p-2 bg-purple-50 rounded-lg">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500 mb-1 block">Adresse</label>
                <p className="text-gray-900 font-medium">{personalInfo.adresse}</p>
              </div>
            </div>

            {/* Date d'inscription */}
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500 mb-1 block">Date d'inscription</label>
                <p className="text-gray-900 font-medium">{formatDate(personalInfo.dateInscription)}</p>
              </div>
            </div>

            {/* Statut */}
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500 mb-1 block">Statut</label>
                <div className="mt-1">
                  {getStatusBadge(personalInfo.statut, personalInfo.vendorStatus)}
                </div>
              </div>
            </div>
          </div>

          {/* Logo si disponible */}
          {personalInfo.logo && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="text-sm text-gray-500 mb-3 block">Logo de l'agence</label>
              <div className="flex justify-center">
                <img
                  src={getAbsoluteUrl(personalInfo.logo)}
                  alt={personalInfo.nom}
                  className="w-32 h-32 rounded-lg object-cover border border-gray-200 shadow-sm"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/128?text=Logo+non+disponible';
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Bloc 2 : Statistiques de Réservation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#27489E]" />
            Statistiques de Réservation
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {statsCards.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div
                  key={index}
                  onClick={() => handleKpiClick(stat)}
                  className={`${stat.bgColor} ${stat.borderColor} border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer`}
                  title={`Cliquer pour voir les réservations ${stat.label.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">{stat.label}</h3>
                    <IconComponent className={`w-5 h-5 ${stat.textColor}`} />
                  </div>
                  <p className={`text-3xl font-bold ${stat.textColor}`}>
                    {stat.value}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AgencyDetails;
